import {
  Client,
  ClientOptions,
  createClient,
  FormattedExecutionResult,
  SubscribePayload,
} from "graphql-ws";
import {
  AuthSession,
  AuthSessionExpiredError,
  AuthSessionState,
} from "../auth/oauth/AuthSession";

interface SubscriptionAuthClient {
  getSession(): Promise<AuthSession>;
  rejectSession(): Promise<void>;
}

interface CloseLike {
  code?: number;
}

interface SubscriptionAuthController {
  cancelPending(): boolean;
  hasPending(): boolean;
  options: Pick<
    ClientOptions,
    "connectionParams" | "retryAttempts" | "shouldRetry"
  >;
}

type AuthenticatedClientOptions = Omit<
  ClientOptions,
  "connectionParams" | "lazy" | "retryAttempts" | "shouldRetry"
> & {
  reconnect: boolean;
  onExpiredSession: () => void;
  refreshRetryDelayMs?: number;
};

class SubscriptionAuthCancelledError extends Error {
  constructor() {
    super("Subscription auth wait cancelled");
    this.name = "SubscriptionAuthCancelledError";
  }
}

const isForbiddenClose = (error: unknown) =>
  typeof error === "object" &&
  error !== null &&
  (error as CloseLike).code === 4403;

const waitForRetry = (delayMs: number, signal: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(new SubscriptionAuthCancelledError());
      return;
    }
    const onAbort = () => {
      globalThis.clearTimeout(timeout);
      reject(new SubscriptionAuthCancelledError());
    };
    const timeout = globalThis.setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, delayMs);
    signal.addEventListener("abort", onAbort, { once: true });
  });

export const makeSubscriptionAuthController = (
  authClient: SubscriptionAuthClient,
  {
    reconnect,
    onExpiredSession,
    refreshRetryDelayMs = 1_000,
    hasActiveSubscribers = () => true,
  }: {
    reconnect: boolean;
    onExpiredSession: () => void;
    refreshRetryDelayMs?: number;
    hasActiveSubscribers?: () => boolean;
  },
): SubscriptionAuthController => {
  let lastConnectionState = AuthSessionState.ANONYMOUS;
  let pendingController: AbortController | undefined;
  let expirationHandled = false;

  const cancelPending = () => {
    if (!pendingController) {
      return false;
    }
    pendingController.abort();
    pendingController = undefined;
    return true;
  };

  const expireSessionOnce = (rejectSession: boolean) => {
    if (expirationHandled) {
      return;
    }
    expirationHandled = true;
    if (rejectSession) {
      void authClient.rejectSession().then(
        onExpiredSession,
        onExpiredSession,
      );
      return;
    }
    onExpiredSession();
  };

  return {
    cancelPending,
    hasPending: () => !!pendingController,
    options: {
      retryAttempts: reconnect ? Number.MAX_SAFE_INTEGER : 0,
      connectionParams: async () => {
        const controller = new AbortController();
        pendingController = controller;
        try {
          for (;;) {
            // Refresh attempts are deliberately serialized through the auth client.
            // eslint-disable-next-line no-await-in-loop
            const session = await authClient.getSession();
            lastConnectionState = session.state;

            if (session.state === AuthSessionState.EXPIRED) {
              expireSessionOnce(false);
              throw new AuthSessionExpiredError();
            }
            if (session.state === AuthSessionState.REFRESH_UNAVAILABLE) {
              // Keep the graphql-ws connection attempt pending until recovery.
              // eslint-disable-next-line no-await-in-loop
              await waitForRetry(refreshRetryDelayMs, controller.signal);
              continue;
            }
            if (session.state === AuthSessionState.AUTHENTICATED) {
              expirationHandled = false;
              return { accessToken: session.accessToken };
            }
            return {};
          }
        } finally {
          if (pendingController === controller) {
            pendingController = undefined;
          }
        }
      },
      shouldRetry: (error) => {
        cancelPending();
        if (!reconnect || !hasActiveSubscribers()) {
          return false;
        }
        if (error instanceof AuthSessionExpiredError) {
          return false;
        }
        if (isForbiddenClose(error)) {
          if (lastConnectionState === AuthSessionState.AUTHENTICATED) {
            expireSessionOnce(true);
          }
          return false;
        }
        return true;
      },
    },
  };
};

export const makeSubscriptionAuthOptions = (
  authClient: SubscriptionAuthClient,
  options: {
    reconnect: boolean;
    onExpiredSession: () => void;
    refreshRetryDelayMs?: number;
  },
) => makeSubscriptionAuthController(authClient, options).options;

export const createAuthenticatedSubscriptionsClient = (
  authClient: SubscriptionAuthClient,
  {
    reconnect,
    onExpiredSession,
    refreshRetryDelayMs,
    ...clientOptions
  }: AuthenticatedClientOptions,
): Client => {
  let activeSubscribers = 0;
  const auth = makeSubscriptionAuthController(authClient, {
    reconnect,
    onExpiredSession,
    refreshRetryDelayMs,
    hasActiveSubscribers: () => activeSubscribers > 0,
  });

  const rawClient = createClient({
    ...clientOptions,
    lazy: true,
    ...auth.options,
  });

  const releaseSubscriber = () => {
    activeSubscribers = Math.max(0, activeSubscribers - 1);
    if (activeSubscribers === 0 && auth.hasPending()) {
      rawClient.terminate();
      auth.cancelPending();
    }
  };

  const subscribe: Client["subscribe"] = (payload, sink) => {
    activeSubscribers += 1;
    let released = false;
    let cancelled = false;
    const release = () => {
      if (!released) {
        released = true;
        releaseSubscriber();
      }
    };
    const dispose = rawClient.subscribe(payload, {
      next: sink.next,
      error: (error) => {
        release();
        if (!cancelled) {
          sink.error(error);
        }
      },
      complete: () => {
        release();
        if (!cancelled) {
          sink.complete();
        }
      },
    });

    return () => {
      cancelled = true;
      dispose();
      release();
    };
  };

  const iterate = <Data = Record<string, unknown>, Extensions = unknown>(
    payload: SubscribePayload,
  ): AsyncIterableIterator<FormattedExecutionResult<Data, Extensions>> => {
    activeSubscribers += 1;
    const iterator = rawClient.iterate<Data, Extensions>(payload);
    let released = false;
    const release = () => {
      if (!released) {
        released = true;
        releaseSubscriber();
      }
    };

    return {
      async next(...args) {
        const result = await iterator.next(...args);
        if (result.done) {
          release();
        }
        return result;
      },
      async return(value) {
        const result = iterator.return
          ? await iterator.return(value)
          : { done: true as const, value };
        release();
        return result;
      },
      async throw(error) {
        try {
          if (iterator.throw) {
            return await iterator.throw(error);
          }
          throw error;
        } finally {
          release();
        }
      },
      [Symbol.asyncIterator]() {
        return this;
      },
    };
  };

  return {
    on: rawClient.on,
    subscribe,
    iterate,
    terminate: rawClient.terminate,
    async dispose() {
      activeSubscribers = 0;
      if (auth.hasPending()) {
        rawClient.terminate();
      }
      auth.cancelPending();
      await rawClient.dispose();
    },
  };
};
